// An agent changing what the user sees, because they asked it to (show, worker/mcp.ts; David, 16 Oct): a board, a
// widget brought into view, a doc board's page, full screen, or All boards. The board sends it to the tab the
// user used last; this tab does it as a click would.

import { kindOf } from "../../shared/boardKinds";
import type { ServerEvent } from "../../shared/types";
import { openPage } from "../boards/doc/state";
import { camera } from "../canvas/camera";
import { itemRect } from "../canvas/geometry";
import { enterFullscreen, exitFullscreen } from "../chrome/Fullscreen";
import { openOverview } from "../chrome/Panels";
import { switchBoard } from "./actions";
import { get, glowItems, set, useStore } from "./store";
import { useEffect } from "react";
import { useDocUi } from "../boards/doc/state";
import { DEMO } from "../demo/mode";
import { chat, chatOpensBoard } from "../agent/store";

export function navigateTo(ev: Extract<ServerEvent, { type: "navigate" }>) {
  const s = get();
  // All boards closes for a board it was asked to open, as a click on one would
  if ((ev.board || ev.widget || ev.page) && s.panel === "boards") set({ panel: null, panelArg: null });
  const boardId = ev.board && s.boards.some((b) => b.id === ev.board) ? ev.board : null;
  // shown while the chat on screen is replying: its agent's doing, so the chat stays on screen, its reply going on, and
  // a board that reply made becomes the chat's (Fixes 9 fix round)
  if (boardId && boardId !== s.boardId && chat.get().status === "running") chatOpensBoard();
  if (boardId && boardId !== s.boardId) switchBoard(boardId);
  if (boardId && ev.page) openPage(boardId, ev.page);
  const item = ev.widget ? get().items[ev.widget] : null;
  if (item) {
    set({ selection: [item.id] });
    glowItems([item.id]);
    const board = get().boards.find((b) => b.id === item.boardId);
    // after the board has drawn: on a canvas the camera goes to it, elsewhere (a shelf, a page) it scrolls into view
    setTimeout(() => {
      if (board && kindOf(board) === "canvas") camera.fit(itemRect(item), { maxZ: 1, pad: 140 });
      else document.querySelector(`[data-item-id="${CSS.escape(item.id)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 250);
  }
  if (ev.fullscreen === true && !get().presenting) setTimeout(() => void enterFullscreen(), 300);
  if (ev.fullscreen === false && get().presenting) exitFullscreen();
  if (ev.overview) setTimeout(() => openOverview(), ev.fullscreen === false ? 300 : 0);
}

/**
 * Each board, and a doc board's open page, at an address of its own (Fixes 10): /?board=<id>&page=<id>. It can be
 * bookmarked, opened in a new tab or sent, and Back and Forward move between boards and pages. The first address a
 * load settles on replaces the one it came with; each move after is a step Back takes. On a phone, Back closes what
 * is open over the board (src/phone/PhoneChrome.tsx), so there the address only follows the board and adds no steps.
 */
let settled = false;

export function useBoardAddress() {
  const boardId = useStore((s) => s.boardId);
  const page = useDocUi((s) => (boardId ? s.open[boardId] : undefined));
  // a page named in the address it opened with is the doc board's open page
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const b = p.get("board");
    const pg = p.get("page");
    if (b && pg) openPage(b, pg);
  }, []);
  useEffect(() => {
    if (!boardId || DEMO) return;
    const url = new URL(location.href);
    // a load already at this board's address keeps it as it is, its page too, while that page opens
    if (!settled && url.searchParams.get("board") === boardId) {
      settled = true;
      return;
    }
    if (url.searchParams.get("board") === boardId && (url.searchParams.get("page") ?? undefined) === page) return;
    url.searchParams.set("board", boardId);
    if (page) url.searchParams.set("page", page);
    else url.searchParams.delete("page");
    const to = url.pathname + url.search + url.hash;
    // the first address a page load settles on replaces the one it came with, a reload's too
    if (settled && !get().mobile) history.pushState({ cbBoard: boardId }, "", to);
    else history.replaceState({ ...(history.state ?? {}), cbBoard: boardId }, "", to);
    settled = true;
  }, [boardId, page]);
  useEffect(() => {
    const onPop = () => {
      if (DEMO) return;
      // on a phone, Back closes what is open (src/phone/PhoneChrome.tsx), and the step it lands on can name the board
      // that was in view before: the address is put back to the board in view now (review)
      if (get().mobile) {
        const s = get().boardId;
        const url = new URL(location.href);
        if (s && url.searchParams.get("board") !== s) {
          url.searchParams.set("board", s);
          url.searchParams.delete("page");
          history.replaceState(history.state, "", url.pathname + url.search + url.hash);
        }
        return;
      }
      const p = new URLSearchParams(location.search);
      const b = p.get("board");
      if (!b) return;
      if (b !== get().boardId) switchBoard(b);
      const pg = p.get("page");
      if (pg && useDocUi.getState().open[b] !== pg) openPage(b, pg);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
}
